import React from 'react';
import RadioButton from '$/components/ui/RadioButton';
import tw from 'twin.macro';
import { css } from '@emotion/react';
import { useThemeContext } from '../../ThemeContext';
import Settings from '../../Settings';

export interface RadioOption {
  value: ((value: unknown) => boolean) | unknown;
  key: string;
  disabled?: boolean;
  title: string | JSX.Element;
  image?: string;
  id?: string;
}

export interface RadioOptionsProps {
  value?: unknown;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  optionsClassName?: string;
  options: RadioOption[];
  horizontal?: boolean;
}

export const RadioImageOptions: React.FC<RadioOptionsProps> = ({
  value,
  onChange,
  className,
  optionsClassName,
  options,
}) => {
  const etheme = useThemeContext();

  return (
    <div
      className={className}
      css={css`
        display: grid;
        grid-template-columns: repeat(auto-fill, 180px);
        grid-gap: 2rem;
      `}
    >
      {options.map((item) => {
        const selected = typeof item.value === 'function' ? item.value(value) : value == item.value;

        return (
          <label
            key={item.key}
            className={`
              ${optionsClassName ?? ''} 
              ${item.disabled && 'disabled'} 
              ${selected && 'selected'}
            `}
            css={css`
              &.selected {
                img {
                  border: 2px solid ${etheme.controls.border.selected};
                }
              }
              &.disabled {
                .radio,
                .setting.toggle {
                  opacity: 0.45;
                }
              }
            `}
          >
            <div className='flex items-center cursor-pointer'>
              <RadioButton.Option
                value={item.value}
                checked={selected}
                onChange={onChange}
                disabled={item.disabled}
              />
              <div className='flex flex-col settings toggle'>
                <img
                  className='rounded-lg'
                  id={item.id}
                  css={css`
                    width: 180px;
                    height: 180px;
                    border: 1px solid ${etheme.controls.border.unselected};
                  `}
                  src={item.image}
                />
                <div className='flex my-1/2e'>{item.title}</div>
              </div>
            </div>
          </label>
        );
      })}
    </div>
  );
};

const RadioOptions: React.FC<RadioOptionsProps> = ({
  value,
  onChange,
  className,
  optionsClassName,
  options,
  horizontal,
}) => {
  return (
    <div className={className} css={[tw`flex`, horizontal ? tw`flex-row flex-wrap w-full` : null]}>
      {options.map((item) => {
        const isChecked =
          typeof item.value === 'function' ? item.value(value) : value == item.value;
        return (
          <label
            key={item.key}
            className={`mr-1/2e ${optionsClassName ?? ''} ${item.disabled && 'disabled'} ${
              isChecked && 'checked'
            }`}
            css={css`
              &.disabled {
                .radio,
                .setting.toggle {
                  opacity: 0.45;
                }
              }
              &.checked .setting.toggle {
                color: #000000f2;
              }
            `}
          >
            <div className='flex items-center cursor-pointer'>
              <RadioButton.Option
                value={item.value}
                checked={isChecked}
                onChange={onChange}
                disabled={item.disabled}
              />
              <RadioButton id={item.id} />
              <Settings.Toggle
                className='ml-1/2e mr-1e'
                css={css`
                  ${/* Matches .ui.checkbox label */ ''}
                  transition: color 200ms ease-out;
                  font-size: 1em;
                  line-height: 17px;
                `}
              >
                {item.title}
              </Settings.Toggle>
            </div>
          </label>
        );
      })}
    </div>
  );
};

export default RadioOptions;
